import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Users,
  Settings,
  Columns,
  Code,
  Bot,
  Shield,
  Building2,
  BarChart3,
  GitBranch,
  Info,
  MessageSquare,
  Megaphone,
} from "lucide-react";
import "./AdminPanel.css";
import GdprConfigPanel from "../components/GdprConfigPanel";
import {
  UserManagementTab,
  DepartmentManagementTab,
  SystemSettingsTab,
  StageManagementTab,
  SkillManagementTab,
  PromptManagementTab,
  CompanySettingsTab,
  TenantAnalyticsTab,
  TransitionRulesTab,
  AboutTab,
  FeedbackManagementTab,
  ReleaseNotesTab,
} from "./Admin";
import type { AdminTab } from "../types/admin.types";

/**
 * AdminPanel - Premium recruitment intelligence admin interface
 *
 * Features:
 * - Tab-based navigation for different admin sections
 * - Responsive design with mobile support
 * - Keyboard accessible with focus states
 * - WCAG 2.1 compliant
 */
const AdminPanel: React.FC = () => {
  const { t } = useTranslation("admin");
  const [activeTab, setActiveTab] = useState<AdminTab>("users");

  // Tab configuration with icons and labels
  const tabs: Array<{ id: AdminTab; label: string; icon: React.ReactNode }> = [
    { id: "company", label: t("tabs.company"), icon: <Building2 size={16} /> },
    { id: "users", label: t("tabs.users"), icon: <Users size={16} /> },
    {
      id: "departments",
      label: t("tabs.departments"),
      icon: <Building2 size={16} />,
    },
    {
      id: "analytics",
      label: t("tabs.analytics"),
      icon: <BarChart3 size={16} />,
    },
    { id: "settings", label: t("tabs.settings"), icon: <Settings size={16} /> },
    { id: "stages", label: t("tabs.stages"), icon: <Columns size={16} /> },
    { id: "skills", label: t("tabs.skills"), icon: <Code size={16} /> },
    { id: "prompts", label: t("tabs.prompts"), icon: <Bot size={16} /> },
    { id: "gdpr", label: t("tabs.gdpr"), icon: <Shield size={16} /> },
    {
      id: "transitions",
      label: t("tabs.transitions"),
      icon: <GitBranch size={16} />,
    },
    { id: "about", label: t("tabs.about"), icon: <Info size={16} /> },
    {
      id: "feedback",
      label: t("tabs.feedback"),
      icon: <MessageSquare size={16} />,
    },
    {
      id: "releases",
      label: t("tabs.releases", "Release Notes"),
      icon: <Megaphone size={16} />,
    },
  ];

  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    // Optional: Update URL with tab parameter for deep linking
    // window.history.pushState({}, '', `?tab=${tab}`);
  };

  return (
    <div className="admin-panel">
      {/* Header */}
      <div className="admin-header">
        <h1>{t("panel.title")}</h1>
        <p>{t("panel.subtitle")}</p>
      </div>

      {/* Tab Navigation */}
      <div className="admin-tabs" role="tablist" aria-label="Admin sections">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => handleTabChange(tab.id)}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        {activeTab === "company" && <CompanySettingsTab />}
        {activeTab === "users" && <UserManagementTab />}
        {activeTab === "departments" && <DepartmentManagementTab />}
        {activeTab === "analytics" && <TenantAnalyticsTab />}
        {activeTab === "settings" && <SystemSettingsTab />}
        {activeTab === "stages" && <StageManagementTab />}
        {activeTab === "skills" && <SkillManagementTab />}
        {activeTab === "prompts" && <PromptManagementTab />}
        {activeTab === "gdpr" && (
          <div className="admin-section">
            <GdprConfigPanel />
          </div>
        )}
        {activeTab === "transitions" && <TransitionRulesTab />}
        {activeTab === "about" && <AboutTab />}
        {activeTab === "feedback" && <FeedbackManagementTab />}
        {activeTab === "releases" && <ReleaseNotesTab />}
      </div>
    </div>
  );
};

export default AdminPanel;
